import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, map, of, shareReplay } from 'rxjs';
import { AppConfigService } from './app-config.service';
import { LoggingService } from './logging.service';

/** Checks the remote Okta session via /api/v1/sessions/me. */
@Injectable({ providedIn: 'root' })
export class RemoteSessionCheckerService {
  private readonly http = inject(HttpClient);
  private readonly appConfig = inject(AppConfigService);
  private readonly logger = inject(LoggingService);

  private readonly CACHE_DURATION_MS = 5 * 60 * 1000;
  private cachedResult$: Observable<boolean> | null = null;
  private cachedAt = 0;

  isSessionActive(): Observable<boolean> {
    const oktaBaseUrl = this.appConfig.config?.okta?.issuer;
    if (!oktaBaseUrl) {
      this.logger.warn('[RemoteSessionChecker] No Okta issuer configured. Returning false.');
      return of(false);
    }

    const now = Date.now();
    if (this.cachedResult$ && now - this.cachedAt < this.CACHE_DURATION_MS) {
      return this.cachedResult$;
    }

    const url = `${oktaBaseUrl.replace(/\/oauth2.*/, '')}/api/v1/sessions/me`;
    this.cachedAt = now;
    this.cachedResult$ = this.http.get(url, { withCredentials: true }).pipe(
      map(() => true),
      catchError((err) => {
        this.logger.error('[RemoteSessionChecker] /sessions/me request failed.', err);
        return of(false);
      }),
      shareReplay(1),
    );
    return this.cachedResult$;
  }
}
